import { useRef, useState, type ChangeEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router';
import type { RecipeDetail, RecipeSource } from '@fewerbags/schema';
import { Alert } from '../../components/Alert';
import { AppShell } from '../../components/AppShell';
import { Button, buttonClass } from '../../components/Button';
import { Dialog } from '../../components/Dialog';
import { ClockIcon, StarIcon, UsersIcon } from '../../components/icons';
import { ReportDialog } from '../../components/ReportDialog';
import { Section } from '../../components/Section';
import { Skeleton } from '../../components/Skeleton';
import { CuisineCourseBadges } from '../../components/recipes/CuisineCourseBadges';
import { PersonRatings } from '../../components/recipes/PersonRatings';
import { RecipeNotes } from '../../components/recipes/RecipeNotes';
import { RecipePhoto } from '../../components/recipes/RecipePhoto';
import { RatingSummary } from '../../components/recipes/StarRating';
import { EQUIPMENT_LABEL } from '../../lib/format';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { messageForError } from '../../lib/errors';
import { FALLBACK_SERVINGS } from '../../lib/plans';
import { displayAmount, formatMinutes, scaleFactor, timeSummary } from './recipe-form';
import { recipeKeys } from './queries';
import { formatAvg } from './taxonomy';

function sourceLine(source: RecipeSource): string {
  const bits = [source.siteName, source.author].filter((s): s is string => Boolean(s));
  if (source.kind === 'family' && bits.length === 0) return 'Family recipe';
  if (bits.length === 0) return source.url ?? 'Imported';
  return bits.join(' · ');
}

export function RecipeDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const { auth } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const household = auth.status === 'authed' ? auth.household : null;
  const system = household?.unitSystem === 'metric' ? 'metric' : 'us';

  const recipe = useQuery({
    queryKey: recipeKeys.detail(id),
    queryFn: () => api.recipes.get(id),
    enabled: id !== '',
  });

  const [servings, setServings] = useState<number | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [photoError, setPhotoError] = useState<string | null>(null);

  const cached = (updated: RecipeDetail) => {
    qc.setQueryData(recipeKeys.detail(updated.id), updated);
    void qc.invalidateQueries({ queryKey: recipeKeys.all });
  };

  // Any member rates for anyone in the household (D30).
  const rate = useMutation({
    mutationFn: (v: { personId: string; stars: number }) =>
      api.recipes.ratePerson(id, v.personId, { stars: v.stars }),
    onSuccess: cached,
  });
  const unrate = useMutation({
    mutationFn: (personId: string) => api.recipes.unratePerson(id, personId),
    onSuccess: cached,
  });
  const pendingPersonId = rate.isPending
    ? (rate.variables?.personId ?? null)
    : unrate.isPending
      ? (unrate.variables ?? null)
      : null;
  const setVisibility = useMutation({
    mutationFn: (visibility: 'private' | 'public') => api.recipes.update(id, { visibility }),
    onSuccess: cached,
  });
  const remove = useMutation({
    mutationFn: () => api.recipes.remove(id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: recipeKeys.all });
      qc.removeQueries({ queryKey: recipeKeys.detail(id) });
      void navigate('/recipes', { replace: true });
    },
  });
  const fork = useMutation({
    mutationFn: () => api.recipes.fork(id),
    onSuccess: async (created) => {
      await qc.invalidateQueries({ queryKey: recipeKeys.all });
      void navigate(`/recipes/${created.id}`);
    },
  });
  const photo = useMutation({
    mutationFn: (file: File | null): Promise<RecipeDetail> =>
      file === null ? api.media.removeRecipePhoto(id) : api.media.uploadRecipePhoto(id, file),
    onSuccess: cached,
  });

  if (recipe.isPending) {
    return (
      <AppShell width="wide">
        <div aria-busy className="flex flex-col gap-5">
          <div className="skeleton h-64 rounded-3xl sm:h-80" />
          <Skeleton lines={4} label="Loading recipe" />
        </div>
      </AppShell>
    );
  }
  if (recipe.isError || !recipe.data) {
    return (
      <AppShell title="Recipe">
        <Alert tone="error">{messageForError(recipe.error)}</Alert>
        <Link to="/recipes" className="mt-3 inline-block text-sm text-brand underline">
          Back to recipes
        </Link>
      </AppShell>
    );
  }

  const r = recipe.data;
  const target = servings ?? household?.defaultServings ?? r.servings ?? FALLBACK_SERVINGS;
  const factor = scaleFactor(r.servings, target);
  const times = [
    timeSummary(r.prepMin, r.cookMin),
    r.restMin > 0 ? `rest ${formatMinutes(r.restMin)} before serving` : '',
  ]
    .filter(Boolean)
    .join(' · ');

  function onPickFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setPhotoError('Please choose an image file.');
      return;
    }
    setPhotoError(null);
    photo.mutate(file);
  }

  const totalMin = (r.prepMin ?? 0) + (r.cookMin ?? 0);
  const photoBtn =
    'inline-flex min-h-11 items-center rounded-xl bg-surface/95 px-3 text-sm font-semibold shadow-sm backdrop-blur transition-colors hover:bg-surface disabled:opacity-50';

  return (
    <AppShell width="wide">
      <article className="flex flex-col gap-5 lg:gap-6">
        <header className="relative overflow-hidden rounded-3xl bg-brand shadow-[var(--shadow-lift)]">
          <RecipePhoto
            photoMediaId={r.photoMediaId}
            title={r.title}
            variant="full"
            courses={r.courses}
            cuisines={r.cuisines}
            shape="banner"
            className="h-64 w-full sm:h-80 lg:h-96"
          />
          {/* Teal scrim: the title is white text on (near-solid) teal, per the brand rule. */}
          <div className="on-teal absolute inset-x-0 bottom-0 flex flex-col gap-2 bg-gradient-to-t from-brand via-brand/95 to-transparent px-4 pt-16 pb-4 sm:px-6 sm:pb-6">
            <h1 className="text-2xl leading-tight text-white sm:text-4xl">{r.title}</h1>
            <div className="flex flex-wrap items-center gap-2 [&_li]:ring-1 [&_li]:ring-white/50">
              <CuisineCourseBadges cuisines={r.cuisines} courses={r.courses} />
              {r.visibility === 'public' ? (
                <span className="rounded-full bg-accent px-2.5 py-0.5 text-xs font-semibold text-ink sm:text-sm">
                  Shared
                </span>
              ) : null}
            </div>
          </div>
          {r.own ? (
            <div className="absolute top-3 right-3 flex flex-wrap justify-end gap-2">
              <input
                ref={fileInput}
                type="file"
                accept="image/*"
                className="sr-only"
                onChange={onPickFile}
              />
              <button
                type="button"
                onClick={() => fileInput.current?.click()}
                disabled={photo.isPending}
                className={`${photoBtn} text-brand`}
              >
                {photo.isPending ? 'Working…' : r.photoMediaId ? 'Replace photo' : 'Add photo'}
              </button>
              {r.photoMediaId ? (
                <button
                  type="button"
                  onClick={() => photo.mutate(null)}
                  disabled={photo.isPending}
                  className={`${photoBtn} text-fail`}
                >
                  Remove photo
                </button>
              ) : null}
            </div>
          ) : null}
        </header>
        {photoError ? <Alert tone="error">{photoError}</Alert> : null}
        {photo.isError ? <Alert tone="error">{messageForError(photo.error)}</Alert> : null}

        {r.own && r.hidden ? (
          <Alert tone="warning">
            Other households reported this recipe, so it is hidden from the shared library while we
            review it. You can still use it.
          </Alert>
        ) : null}

        <dl className="grid grid-cols-3 gap-2 sm:gap-3">
          <div className="card flex flex-col items-center gap-1 px-2 py-3 text-center">
            <ClockIcon className="size-5 text-orange-ink" />
            <dt className="text-xs font-medium text-muted">Total time</dt>
            <dd className="text-sm font-bold text-brand sm:text-base">
              {totalMin > 0 ? formatMinutes(totalMin) : '—'}
            </dd>
          </div>
          <div className="card flex flex-col items-center gap-1 px-2 py-3 text-center">
            <UsersIcon className="size-5 text-orange-ink" />
            <dt className="text-xs font-medium text-muted">Serves</dt>
            <dd className="text-sm font-bold text-brand sm:text-base">{r.servings ?? '—'}</dd>
          </div>
          <div className="card flex flex-col items-center gap-1 px-2 py-3 text-center">
            <StarIcon className="size-5 text-orange-ink" />
            <dt className="text-xs font-medium text-muted">Household</dt>
            <dd className="text-sm font-bold text-brand sm:text-base">
              {r.householdRating.avg === null ? (
                '—'
              ) : (
                <>
                  {formatAvg(r.householdRating.avg)}
                  <span className="font-medium text-muted"> / 5</span>
                </>
              )}
            </dd>
          </div>
        </dl>

        {r.description || times || r.tags.length > 0 || r.source || r.forkedFromRecipeId ? (
          <div className="flex flex-col gap-2">
            {r.description ? <p className="text-base text-ink">{r.description}</p> : null}
            {times ? <p className="text-sm text-muted">{times}</p> : null}
            {r.forkedFromRecipeId ? (
              <p className="text-xs text-muted">Copied from another household</p>
            ) : null}
            {r.tags.length > 0 ? (
              <ul className="flex flex-wrap gap-2" aria-label="Tags">
                {r.tags.map((tag) => (
                  <li
                    key={tag}
                    className="rounded-full bg-brand-soft px-2.5 py-0.5 text-xs text-brand"
                  >
                    {tag}
                  </li>
                ))}
              </ul>
            ) : null}
            {r.source ? (
              <p className="text-xs text-muted">
                Source: {sourceLine(r.source)}
                {r.source.url ? (
                  <>
                    {' — '}
                    <a
                      href={r.source.url}
                      target="_blank"
                      rel="noreferrer nofollow"
                      className="text-brand underline underline-offset-2"
                    >
                      original page
                    </a>
                  </>
                ) : null}
                {r.source.licence ? ` (${r.source.licence})` : null}
              </p>
            ) : null}
          </div>
        ) : null}

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] lg:items-start lg:gap-6">
          <Section title="Ingredients" accent="orange" className="lg:sticky lg:top-6">
            <div className="flex flex-wrap items-center gap-2 rounded-xl bg-orange-soft/40 p-2">
              <span id="servings-label" className="pl-1 text-sm font-medium text-ink">
                Scale to
              </span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  aria-label="Fewer servings"
                  onClick={() => setServings(Math.max(1, target - 1))}
                  className="size-11 rounded-xl border border-line bg-surface text-lg text-brand transition-colors hover:bg-brand-soft active:scale-95"
                >
                  <span aria-hidden>−</span>
                </button>
                <input
                  type="number"
                  aria-labelledby="servings-label"
                  min={1}
                  max={200}
                  value={target}
                  onChange={(e) => {
                    const n = Number(e.target.value);
                    setServings(Number.isFinite(n) && n > 0 ? Math.min(200, Math.round(n)) : 1);
                  }}
                  className="min-h-11 w-16 rounded-xl border border-line bg-surface px-2 text-center text-base font-semibold text-ink"
                />
                <button
                  type="button"
                  aria-label="More servings"
                  onClick={() => setServings(Math.min(200, target + 1))}
                  className="size-11 rounded-xl border border-line bg-surface text-lg text-brand transition-colors hover:bg-brand-soft active:scale-95"
                >
                  <span aria-hidden>+</span>
                </button>
              </div>
              <span className="text-sm text-muted">
                {r.servings
                  ? factor === 1
                    ? 'as written'
                    : `×${factor.toFixed(2).replace(/\.?0+$/, '')}`
                  : 'this recipe has no serving count, so amounts are as written'}
              </span>
            </div>
            {r.ingredients.length === 0 ? (
              <p className="text-sm text-muted">No ingredients recorded.</p>
            ) : (
              <ul className="flex flex-col">
                {r.ingredients.map((line) => {
                  const amount = displayAmount(line.qty, line.qtyMax, line.unit, factor, system);
                  return (
                    <li
                      key={line.id}
                      className="flex items-baseline gap-3 border-b border-dashed border-line py-2.5 text-base text-ink last:border-b-0"
                    >
                      <span
                        aria-hidden
                        className="size-2 shrink-0 translate-y-[-1px] rounded-full bg-orange"
                      />
                      <span className="w-24 shrink-0 font-semibold text-brand sm:w-28">
                        {amount}
                      </span>
                      <span className="min-w-0">
                        {line.name}
                        {line.prepNote ? (
                          <span className="text-muted">, {line.prepNote}</span>
                        ) : null}
                        {line.optional ? (
                          <span className="ml-1.5 rounded-full bg-cream px-2 py-0.5 text-xs text-muted">
                            optional
                          </span>
                        ) : null}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </Section>

          <Section title="Steps" accent="orange">
            {r.steps.length === 0 ? (
              <p className="text-sm text-muted">No steps recorded.</p>
            ) : (
              <ol className="flex flex-col gap-4">
                {r.steps.map((step, i) => {
                  const usedNames = step.ingredientRefs
                    .map((refId) => r.ingredients.find((ing) => ing.ingredientId === refId)?.name)
                    .filter((name): name is string => Boolean(name));
                  const chip = 'inline-flex items-center gap-1 rounded-full px-2 py-0.5';
                  return (
                    <li key={step.id} className="relative flex gap-3 sm:gap-4">
                      <span
                        aria-hidden
                        className="flex size-9 shrink-0 items-center justify-center rounded-full bg-orange text-base font-bold text-brand-dark shadow-sm"
                      >
                        {i + 1}
                      </span>
                      <div className="min-w-0 flex-1 rounded-2xl bg-cream/60 px-3 py-2.5">
                        <p className="text-base leading-relaxed text-ink">{step.text}</p>
                        {step.durationMin || step.canDoAhead || step.equipment ? (
                          <p className="mt-2 flex flex-wrap gap-1.5 text-xs">
                            {step.durationMin ? (
                              <span
                                className={`${chip} ${step.timer ? 'bg-orange-soft text-orange-ink' : 'bg-brand-soft text-brand'}`}
                              >
                                {step.timer ? '⏲ ' : <ClockIcon className="size-3.5" />}
                                {formatMinutes(step.durationMin)}
                                {step.attended ? ' hands-on' : ' unattended'}
                              </span>
                            ) : null}
                            {step.canDoAhead ? (
                              <span className={`${chip} bg-accent-soft text-accent-ink`}>
                                can do ahead
                              </span>
                            ) : null}
                            {step.equipment ? (
                              <span className={`${chip} border border-line bg-surface text-ink`}>
                                {EQUIPMENT_LABEL[step.equipment.kind] ?? step.equipment.kind}
                                {step.equipment.count > 1 ? ` ×${step.equipment.count}` : ''}
                                {step.equipment.ovenTempF
                                  ? ` at ${step.equipment.ovenTempF}°F`
                                  : ''}
                                {step.equipment.ovenMode ? ` (${step.equipment.ovenMode})` : ''}
                              </span>
                            ) : null}
                          </p>
                        ) : null}
                        {usedNames.length > 0 ? (
                          <p className="mt-1.5 text-xs text-muted">Uses: {usedNames.join(', ')}</p>
                        ) : null}
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </Section>
        </div>

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 lg:items-start lg:gap-6">
          <Section title="Ratings" accent="orange" icon={<StarIcon className="size-5" />}>
            <PersonRatings
              people={r.personRatings}
              householdRating={r.householdRating}
              pendingPersonId={pendingPersonId}
              onRate={(personId, stars) => rate.mutate({ personId, stars })}
              onClear={(personId) => unrate.mutate(personId)}
            />
            {rate.isError ? <Alert tone="error">{messageForError(rate.error)}</Alert> : null}
            {unrate.isError ? <Alert tone="error">{messageForError(unrate.error)}</Alert> : null}
            {r.visibility === 'public' ? (
              <p className="flex flex-wrap items-center gap-2 text-sm text-muted">
                Everyone on FewerBags:
                <RatingSummary avg={r.rating.avg} count={r.rating.count} />
              </p>
            ) : null}
          </Section>

          <Section
            title="Notes"
            accent="orange"
            description={
              r.own ? undefined : 'Your notes stay with your household, even on a shared recipe.'
            }
          >
            <RecipeNotes recipeId={r.id} notes={r.notes} />
          </Section>
        </div>

        <Section title="Actions" accent="neutral">
          {setVisibility.isError ? (
            <Alert tone="error">{messageForError(setVisibility.error)}</Alert>
          ) : null}
          {fork.isError ? <Alert tone="error">{messageForError(fork.error)}</Alert> : null}
          {remove.isError ? <Alert tone="error">{messageForError(remove.error)}</Alert> : null}
          <div className="flex flex-col gap-2 sm:flex-row">
            {r.own ? (
              <>
                <Link to={`/recipes/${r.id}/edit`} className={`${buttonClass()} sm:flex-1`}>
                  Edit
                </Link>
                <Button
                  variant="secondary"
                  className="sm:flex-1"
                  disabled={setVisibility.isPending}
                  onClick={() =>
                    setVisibility.mutate(r.visibility === 'public' ? 'private' : 'public')
                  }
                >
                  {r.visibility === 'public' ? 'Stop sharing' : 'Share with other households'}
                </Button>
                <Button
                  variant="danger"
                  className="sm:flex-1"
                  onClick={() => setConfirmDelete(true)}
                >
                  Delete
                </Button>
              </>
            ) : (
              <>
                <Button onClick={() => fork.mutate()} disabled={fork.isPending}>
                  Copy to our recipes
                </Button>
                {r.visibility === 'public' ? (
                  <Button variant="secondary" onClick={() => setReporting(true)}>
                    Report
                  </Button>
                ) : null}
              </>
            )}
          </div>
        </Section>
      </article>

      <ReportDialog
        open={reporting}
        onClose={() => setReporting(false)}
        kind="recipe"
        title="Report this recipe"
        description="Tell us what is wrong with this shared recipe."
        send={(input) => api.recipes.report(r.id, input)}
      />

      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)} title="Delete recipe?">
        <p className="text-sm text-ink">
          “{r.title}” will be removed from your household&apos;s recipes. Meal plans that already
          used it keep their history.
        </p>
        <div className="flex gap-2">
          <Button
            variant="danger"
            onClick={() => {
              setConfirmDelete(false);
              remove.mutate();
            }}
            disabled={remove.isPending}
          >
            Delete
          </Button>
          <Button variant="secondary" onClick={() => setConfirmDelete(false)}>
            Keep it
          </Button>
        </div>
      </Dialog>
    </AppShell>
  );
}
